import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { SNFieldValue, SNSmartTeamTicket, SNTicketLocation } from '@nsc/servicenow/types.js';
import { expectedError } from '@nsc/smartteams-shared/api/expected-error.js';
import { of, switchMap } from 'rxjs';
import { ServiceNowTicket, ServiceNowTicketPart } from '../db/ServiceNowTicket.js';
import { TicketLocationCache } from '../db/TicketLocationCache.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsAppPermission, SmartTeamsAppUserGroup } from '../features/permissions/roles-and-permissions.js';
import { getCurrentAppUser, isCustomerOfTicket, snAssignedTo } from './_assignee-guard.js';
import { clearPending, getPending } from './_pending-ops.js';
import { parseLocationCoords } from './_sn-coords.js';
import { ticketChangeTicks } from './_ticket-events.js';
import { attachWorkNotes, renderNotes } from './_ticket-notes.js';

async function geocodeViaGoogle(address: string): Promise<{ lat: number; lng: number } | null> {
  const apiKey = process.env.GOOGLE_API_KEY;
  if (!apiKey) return null;

  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`;
  const res = await fetch(url);
  if (!res.ok) return null;

  const data = (await res.json()) as {
    status: string;
    results?: Array<{ geometry?: { location?: { lat: number; lng: number } } }>;
  };
  if (data.status !== 'OK') return null;

  const location = data.results?.[0]?.geometry?.location;
  return location ? { lat: location.lat, lng: location.lng } : null;
}

/**
 * Resolve a ticket's destination coordinates, preferring the cache before hitting Google.
 * Cache invalidates automatically when `addressLabel` changes for the same ticket.
 *
 * Returns null when the address can't be geocoded (or no API key is configured).
 */
async function resolveTicketLocation(
  ticketNumber: string | undefined,
  addressLabel: string | undefined,
): Promise<{ lat: number; lng: number } | null> {
  if (!ticketNumber || !addressLabel) return null;

  const col = await getRawCollection<TicketLocationCache>(TicketLocationCache._collectionName);
  const cached = await col.findOne({ ticketNumber });
  if (
    cached &&
    cached.addressLabel === addressLabel &&
    typeof cached.lat === 'number' &&
    typeof cached.lng === 'number'
  ) {
    return { lat: cached.lat, lng: cached.lng };
  }

  const geo = await geocodeViaGoogle(addressLabel).catch(() => null);
  if (!geo) return null;

  await col.updateOne(
    { ticketNumber },
    { $set: { ticketNumber, addressLabel, lat: geo.lat, lng: geo.lng, geocodedAt: new Date() } },
    { upsert: true },
  );
  return geo;
}

function composeLocationLabel(loc: SNTicketLocation | undefined): string | undefined {
  if (!loc) return undefined;

  const parts = [loc.street, loc.city, loc.state, loc.zip, loc.country]
    .map(p => p?.display_value?.trim())
    .filter((p): p is string => !!p);
  if (parts.length > 0) return parts.join(', ');

  // Fall back to the reference's formatted address when the components are blank.
  return loc.reference?.display_value?.trim() || undefined;
}

/** Normalise an SN boolean field (`{ value: "1"|"0", display_value: "true"|"false" }`) to a real bool. */
function snBool(field: SNFieldValue | undefined): boolean {
  return field?.value === '1' || field?.display_value?.toLowerCase() === 'true';
}

function mapSNTicket(sn: SNSmartTeamTicket): ServiceNowTicket {
  const assignedTo = snAssignedTo(sn);
  const siteCoords = parseLocationCoords(sn.location);
  const pudoCoords = parseLocationCoords(sn.pudo_location);
  return new ServiceNowTicket({
    number: sn.number?.display_value || undefined,
    title: sn.short_description?.display_value || undefined,
    description: sn.description?.display_value || undefined,
    assignedToSysId: assignedTo?.value || undefined,
    assignedToName: assignedTo?.display_value || undefined,
    state: sn.state?.value || undefined,
    stateLabel: sn.state?.display_value || undefined,
    // On Route / WIP live in the substate while `state` stays put (drives the FE workflow).
    substate: sn.substate?.value || undefined,
    substateLabel: sn.substate?.display_value || undefined,
    etaWindow: sn.eta_window?.value || undefined,
    locationLabel: composeLocationLabel(sn.location),
    // SN returns coordinates directly on the location object — use them when present;
    // getTicketByNumber falls back to Google geocoding only when they're blank. parseLocationCoords
    // also drops SN's 0,0 / 1,1 "no coordinates" placeholders, which would otherwise pin the ticket
    // in the Gulf of Guinea instead of letting it fall through to the geocoder.
    locationLat: siteCoords.lat,
    locationLng: siteCoords.lng,
    // PUDO depot (parts pickup/drop-off) — same flattening as the site location. Empty on list view.
    pudoLocationLabel: composeLocationLabel(sn.pudo_location),
    pudoLocationLat: pudoCoords.lat,
    pudoLocationLng: pudoCoords.lng,
    expectedStart: sn.expected_start?.value || undefined,
    expectedStartDisplay: sn.expected_start?.display_value || undefined,
    // `expected_start` is empty on live tickets; the scheduling window is what carries the date.
    windowStart: sn.scheduling?.window_start?.value || undefined,
    windowEnd: sn.scheduling?.window_end?.value || undefined,
    fulfilmentStatusLabel: sn.fulfilment_status?.display_value || undefined,
    correlationId: sn.serviceContext?.correlation_id?.value || undefined,
    clientSysId: sn.customer?.reference?.value || undefined,
    clientName: sn.customer?.reference?.display_value || undefined,
    clientCode: sn.customer?.code?.display_value || undefined,
    contactName: sn.contact?.reference?.display_value || undefined,
    contactPhone: sn.contact?.phone?.display_value || undefined,
    contactEmail: sn.contact?.email?.display_value || undefined,
    // Part requirements (ticket_details only — folded in from the `part_requirements` sibling by
    // getTicketDetails). Every SN leaf is the `{ value, display_value }` wrapper; booleans are
    // normalised via snBool. `description` falls back to the model number when SN omits a label.
    parts: (sn.parts ?? []).map(
      p =>
        new ServiceNowTicketPart({
          description: p.short_description?.display_value || p.model_number?.display_value || undefined,
          modelNumber: p.model_number?.display_value || undefined,
          manufacturer: p.manufacturer?.display_value || undefined,
          quantity: p.required_quantity?.display_value || undefined,
          stage: p.stage?.display_value || undefined,
          sourced: snBool(p.sourced),
          delivered: snBool(p.delivered),
          returnDefective: snBool(p.return_defective),
          assetLocation: p.asset?.location?.display_value || undefined,
          poNumber: p.asset?.po_number?.display_value || undefined,
        }),
    ),
  });
}

/**
 * State codes treated as "cancelled" and hidden from the overview list. Verified against
 * staging SN data (probe 2026-05-06): state.value="7" → display_value="Cancelled". Once a
 * cancellation is requested and accepted, the WOT transitions to this state.
 *
 * Direct-link access via getTicketByNumber stays open so an admin clicking an old link
 * can still inspect the cancelled record. Only the list view applies this filter.
 */
const HIDDEN_STATE_VALUES = new Set<string>(['7']);
const isHiddenInOverview = (sn: SNSmartTeamTicket): boolean =>
  !!sn.state?.value && HIDDEN_STATE_VALUES.has(sn.state.value);

// Overview list ordering. SN can only sort by a real field (not the app's old custom flow order),
// so we sort soonest-scheduled-first server-side; status chips narrow to a stage when needed.
const LIST_SORT_BY = 'expected_start';
const LIST_SORT_DIRECTION = 'asc' as const;
// Page size, and the SN-imposed ceiling on `limit` (spec §4.5: max 200).
const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 200;

/**
 * Strip ServiceNow encoded-query operators (`^`, `=`) from free-text search so a search term
 * can never alter the query's structure (or widen scoping). Collapses them to spaces and trims.
 */
function sanitizeSearchTerm(raw: string | null | undefined): string {
  return (raw ?? '').replace(/[\^=]/g, ' ').trim();
}

/**
 * Build the encoded SN `ticket_list` query for the overview. AND-ed conditions (scoping + status)
 * come first; the free-text search is appended LAST as an `^OR` group so the OR chaining stays
 * scoped to the search fields and can't widen the AND-ed conditions before it. (SN encoded-query
 * semantics: `a^b^ORc` = `a AND (b OR c)`.) Customer scoping is handled by the `client_id`
 * parameter instead, so it never appears here.
 */
function buildTicketListQuery(opts: {
  assignedTo?: string;
  state?: string;
  substate?: string;
  search?: string;
}): string {
  const conds: string[] = [];
  if (opts.assignedTo) conds.push(`assigned_to=${opts.assignedTo}`);
  // A specific status filter already excludes Cancelled; otherwise hide Cancelled explicitly.
  if (opts.state) conds.push(`state=${opts.state}`);
  else conds.push(`state!=${[...HIDDEN_STATE_VALUES][0]}`);
  if (opts.substate) conds.push(`substate=${opts.substate}`);

  let query = conds.join('^');

  const term = sanitizeSearchTerm(opts.search);
  if (term) {
    const orGroup = [`numberLIKE${term}`, `short_descriptionLIKE${term}`, `assigned_to.nameLIKE${term}`].join('^OR');
    query = query ? `${query}^${orGroup}` : orGroup;
  }
  return query;
}

const clampLimit = (limit: number | null | undefined): number =>
  Math.min(MAX_PAGE_SIZE, Math.max(1, limit ?? DEFAULT_PAGE_SIZE));
const clampOffset = (offset: number | null | undefined): number => Math.max(0, offset ?? 0);

/**
 * Fetches a single page of tickets visible to the current user, filtered, searched and sorted
 * server-side (so paging, search and status filters span the whole instance — not just a first
 * page fetched into the browser). Returns at most `limit` rows; the client treats a short page
 * as "no further pages".
 *
 * Scoping:
 * - Admin: all tickets
 * - FE: only tickets assigned to their ServiceNow FE ID (`assigned_to` in the query)
 * - Customer: tickets owned by any of their linked customers (`client_id` parameter)
 *
 * `search` matches ticket number / short description / engineer name. `state` (+ optional
 * `substate`) narrow to one status. Cancelled tickets are excluded from every branch unless a
 * status filter explicitly selects them (still reachable via getTicketByNumber).
 */
export const getMyTickets = registerQuery({
  operationName: 'getSmartTeamsAppMyTickets',
  type: () => [ServiceNowTicket],
  params: [
    { name: 'search', type: () => String },
    { name: 'state', type: () => String },
    { name: 'substate', type: () => String },
    { name: 'limit', type: () => Int },
    { name: 'offset', type: () => Int },
  ],
  resolve:
    (search?: string, state?: string, substate?: string, limit?: number, offset?: number) =>
    async (_gqlFields: BaseQLSelectionSet<ServiceNowTicket>) => {
      const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.TicketRead));
      if (!allowed) throw expectedError('Insufficient permissions to view tickets');

      const appUser = await getCurrentAppUser();

      const { getTicketList } = await import('@nsc/servicenow/smartteam-api.js');

      const pageLimit = clampLimit(limit);
      const pageOffset = clampOffset(offset);
      const page = { limit: pageLimit, offset: pageOffset, sortBy: LIST_SORT_BY, sortDirection: LIST_SORT_DIRECTION };

      switch (appUser.userGroup) {
        case SmartTeamsAppUserGroup.Admin: {
          const query = buildTicketListQuery({ state, substate, search });
          const snTickets = await getTicketList({ ...page, query });
          return snTickets.filter(t => !isHiddenInOverview(t)).map(mapSNTicket);
        }

        case SmartTeamsAppUserGroup.FieldEngineer: {
          if (!appUser.serviceNowFEId) return [];
          const query = buildTicketListQuery({ assignedTo: appUser.serviceNowFEId, state, substate, search });
          const snTickets = await getTicketList({ ...page, query });
          // Keep the JS assigned_to check as a fail-closed guard against an SN-side query regression.
          const mine = snTickets
            .filter(t => snAssignedTo(t)?.value === appUser.serviceNowFEId && !isHiddenInOverview(t))
            .map(mapSNTicket);
          // Work notes are internal, so only the assigned FE gets them here (never the customer branch
          // below). One `ticket_details` call per ticket, bounded; the admin list is left without notes
          // so a 50-row page doesn't fan out 50 calls on every search keystroke.
          await attachWorkNotes(mine);
          return mine;
        }

        case SmartTeamsAppUserGroup.Customer: {
          const { resolveCustomerSysIds } = await import('./_customer-membership.js');
          const sysIds = await resolveCustomerSysIds(appUser);
          if (sysIds.length === 0) return [];
          const sysIdSet = new Set(sysIds);
          const query = buildTicketListQuery({ state, substate, search });
          // Fail-closed against an SN-side client_id regression: drop rows whose client sys_id is
          // present but not ours. (Keep rows where SN omits `customer`, to avoid false-hiding.)
          const keep = (t: SNSmartTeamTicket): boolean => {
            if (isHiddenInOverview(t)) return false;
            const clientSysId = t.customer?.reference?.value;
            return !clientSysId || sysIdSet.has(clientSysId);
          };

          if (sysIds.length === 1) {
            const list = await getTicketList({ ...page, clientId: sysIds[0], query });
            return list.filter(keep).map(mapSNTicket);
          }

          // Multi-customer: the SN API filters by a single client_id, so "client IN (...)" can't be
          // one paged call. Query each client for the same window and merge — paging across the
          // merged set is approximate (rare: most customers map to a single client).
          const lists = await Promise.all(sysIds.map(clientId => getTicketList({ ...page, clientId, query })));
          const dedup = new Map<string, SNSmartTeamTicket>();
          for (const list of lists) {
            for (const t of list) {
              if (!keep(t)) continue;
              const key = t.number?.value ?? t.number?.display_value;
              if (key && !dedup.has(key)) dedup.set(key, t);
            }
          }
          return [...dedup.values()].slice(0, pageLimit).map(mapSNTicket);
        }

        default:
          return [];
      }
    },
});

/**
 * Fetch a single ticket by number, with ownership check.
 * - Admin: any ticket
 * - FE: only if assigned to them
 */
export const getTicketByNumber = registerQuery({
  operationName: 'getSmartTeamsAppTicketByNumber',
  type: () => ServiceNowTicket,
  params: [{ name: 'ticketNumber', type: () => String }],
  resolve: (ticketNumber: string) => async (_gqlFields: BaseQLSelectionSet<ServiceNowTicket>) => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.TicketRead));
    if (!allowed) throw expectedError('Insufficient permissions to view ticket');

    const appUser = await getCurrentAppUser();

    const { getTicketDetails } = await import('@nsc/servicenow/smartteam-api.js');
    const snTicket = await getTicketDetails(ticketNumber);
    const ticket = mapSNTicket(snTicket);

    const isAdmin = appUser.userGroup === SmartTeamsAppUserGroup.Admin;
    const isAssignedFE =
      appUser.userGroup === SmartTeamsAppUserGroup.FieldEngineer &&
      !!appUser.serviceNowFEId &&
      ticket.assignedToSysId === appUser.serviceNowFEId;
    const customerAccess = await isCustomerOfTicket({
      appUser,
      ticketNumber: ticket.number,
      ticketClientSysId: ticket.clientSysId,
    });

    if (!isAdmin && !isAssignedFE && !customerAccess) {
      throw expectedError('You do not have access to this ticket');
    }

    // Work notes are internal (`wm_task.work_notes`, "internal only" in the Cegeka spec), so only the
    // assigned FE and admins get them — never a customer, who can open this same ticket. A failed
    // lookup leaves the notes empty rather than failing the whole page.
    if (isAdmin || isAssignedFE) {
      try {
        const { getTicketNotes } = await import('@nsc/servicenow/smartteam-api.js');
        const { workNotes } = await getTicketNotes(ticketNumber);
        ticket.workNotes = renderNotes(workNotes);
      } catch {
        ticket.workNotes = [];
      }
    }

    // Per-customer opt-in "Report a problem" buttons — drives which exception buttons the FE
    // sees on the detail page; the action mutations enforce the same set server-side.
    const { getEnabledProblemReports } = await import('./_problem-reports.js');
    ticket.enabledProblemReports = await getEnabledProblemReports(ticket.clientSysId);

    // SN now returns coordinates on the location object (mapped above). Only geocode the address
    // ourselves as a fallback for tickets where SN left latitude/longitude blank.
    if ((ticket.locationLat == null || ticket.locationLng == null) && ticket.locationLabel) {
      const geo = await resolveTicketLocation(ticket.number ?? undefined, ticket.locationLabel);
      if (geo) {
        ticket.locationLat = geo.lat;
        ticket.locationLng = geo.lng;
      }
    }

    // Surface an in-flight async action as a frozen overlay, but self-heal: release it once SN has
    // advanced past the captured state/substate (the substate check covers en_route's ∅ → 8 move).
    const pendingOp = getPending(ticketNumber);
    if (pendingOp) {
      const stateAdvanced = !!ticket.state && ticket.state !== pendingOp.preState;
      // Require a non-empty substate so a transient clear (mid-move) can't release the overlay early.
      const substateAdvanced = !!ticket.substate && ticket.substate !== pendingOp.preSubstate;
      if (stateAdvanced || substateAdvanced) {
        clearPending(ticketNumber);
      } else {
        ticket.pendingAction = pendingOp.action;
      }
    }

    return ticket;
  },
});

/**
 * Live "this ticket changed" signal. Emits an incrementing tick whenever the inbound SN
 * webhook (or an FE action) publishes a change for `ticketNumber`. The client subscribes
 * while a ticket detail page is open and re-runs getTicketByNumber on each tick — so a ticket
 * updates itself when ServiceNow changes, without constant polling. Carries no ticket data
 * (getTicketByNumber still enforces per-ticket access on the re-fetch); we gate the signal on
 * the coarse TicketRead permission for parity.
 */
export const subscribeTicketChanges = registerSubscription({
  operationName: 'subscribeSmartTeamsAppTicketChanges',
  type: () => Int,
  params: [{ name: 'ticketNumber', type: () => String }],
  resolve: (ticketNumber: string) => () =>
    of(null).pipe(
      switchMap(async () => {
        const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.TicketRead));
        if (!allowed) throw expectedError('Insufficient permissions to watch ticket');
        return ticketChangeTicks(ticketNumber);
      }),
      switchMap(obs => obs),
    ),
});
